import { RobotIcon } from "@phosphor-icons/react/dist/csr/Robot";
import { ChatMarkdown } from "@/core/components/chat-markdown";
import { Stack } from "@volca/ui/components/ui/stack";
import { Text } from "@volca/ui/components/ui/text";
import { cn } from "@volca/ui/lib/utils";
import { dayjs } from "@/core/utils/date";
import { packChatAuthorName } from "./message-author";
import { PackChatSource } from "./pack-chat-source";
import type { PackChatSourceAccess } from "./types";
import type { IPackChatMessage } from "./types";

const SHARED_TIMESTAMP_MINUTES = 15;

const messageTime = (at: string) => {
  const time = dayjs(at);
  return time.isSame(dayjs(), "year")
    ? time.format("MMM D, h:mm A")
    : time.format("MMM D, YYYY, h:mm A");
};

/**
 * An event is history rather than something someone said, so it reads as one
 * muted line instead of borrowing the author-and-timestamp shape of a message.
 */
const PackChatEventRow = ({ message }: { message: IPackChatMessage }) => (
  <Text variant="body" tone="muted">
    {message.body.replace(/\.$/, "")} · {messageTime(message.created_at)}
  </Text>
);

/** A message transcript grouped into turns by author and time. */
export const PackChatTranscript = ({
  messages,
  sourceAccess,
}: {
  messages: IPackChatMessage[];
  sourceAccess?: PackChatSourceAccess;
}) => (
  <Stack
    className="w-full"
    align="start"
    gap={0}
    role="log"
    aria-label="Message history"
  >
    {messages.map((message, index) => {
      const previous = messages[index - 1];
      const isEvent = Boolean(message.event_type);
      const isAi = message.author_type === "ai" && !isEvent;
      const continuation =
        previous &&
        !previous.event_type &&
        !isEvent &&
        previous.author_type === message.author_type &&
        previous.author?.id === message.author?.id &&
        dayjs(message.created_at).diff(previous.created_at, "minute") <
          SHARED_TIMESTAMP_MINUTES;

      return (
        <Stack
          key={message.id}
          gap={0}
          align="stretch"
          className={cn("w-full", "min-w-0", {
            "pt-0": index === 0,
            "pt-1.5": index > 0 && continuation,
            "pt-4": index > 0 && !continuation,
          })}
        >
          {isEvent ? (
            <PackChatEventRow message={message} />
          ) : (
            <>
              {!continuation && (
                <Stack
                  direction="row"
                  align="baseline"
                  justify="between"
                  gap={2}
                  className="w-full pb-0.5"
                >
                  <Stack
                    direction="row"
                    align="center"
                    gap={1}
                    className="min-w-0"
                  >
                    {isAi && (
                      <RobotIcon
                        className="size-4 shrink-0 text-subtle"
                        aria-hidden
                      />
                    )}
                    <Text
                      variant="label"
                      className="min-w-0 truncate"
                      title={packChatAuthorName(message)}
                    >
                      {packChatAuthorName(message)}
                    </Text>
                  </Stack>
                  <Text variant="caption" tone="muted" className="shrink-0">
                    {messageTime(message.created_at)}
                  </Text>
                </Stack>
              )}
              {isAi ? (
                // AI answers arrive as structured Markdown, rendered the same way
                // as the run explanation chat so both AI surfaces read alike.
                <ChatMarkdown>{message.body}</ChatMarkdown>
              ) : (
                <Text className="leading-relaxed wrap-anywhere wrap-break-word whitespace-pre-wrap">
                  {message.body}
                </Text>
              )}
              {/* Only a manager reviewing someone else's pay is shown where the
                  answer came from. A technician is already looking at the pack
                  it was built from, so citing it back to them is noise. */}
              {isAi &&
                sourceAccess &&
                message.sources?.map((source, sourceIndex) => (
                  <PackChatSource
                    key={`${source.statement_sha256}-${sourceIndex}`}
                    source={source}
                    access={sourceAccess}
                  />
                ))}
            </>
          )}
        </Stack>
      );
    })}
  </Stack>
);
